import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Send, Upload, Wrench } from 'lucide-react';
import { buildDeviceChatContext, mockDeviceChatReply } from './deviceChatContext';
import {
  getCannedAnswerForSuggestedQuestion,
  getSuggestedQuestionLabels,
} from './deviceHelpSuggestedAnswers';
import { DeviceChatMarkdown } from './DeviceChatMarkdown';
import aiAssistantImg from './assets/ai.png';

/**
 * UI aligned with document-chatbot/src/app/page.tsx (card, bubbles, composer).
 * Assistant avatar: same asset as document-chatbot/public/ai.png (imported so dev-server proxy
 * does not forward /ai.png to medtech.healtek.com).
 * API: REACT_APP_DEVICE_CHAT_API_URL, REACT_APP_DEVICE_CHAT_BEARER — same as before.
 */
function deriveUploadUrl(chatUrl) {
  if (!chatUrl) return '';
  if (chatUrl.includes('pubDevice/chat')) {
    return chatUrl.replace(/pubDevice\/chat/g, 'pubDevice/upload');
  }
  if (chatUrl.includes('device-chat')) {
    return chatUrl.replace(/device-chat/g, 'device-upload');
  }
  return '';
}

function buildLanguageSystemPrompt(locale) {
  const lang = locale === 'cs' ? 'Czech' : locale === 'de' ? 'German' : 'English';
  return [
    `You are a device support assistant. Always answer in ${lang}.`,
    `Do not switch to another language even if user input is mixed or ambiguous.`,
    'Respond in this exact tool format and nothing else:',
    '{"answer":"<user visible answer in selected language>","needsService":<true|false>}',
    'Set needsService=true whenever the user still needs unresolved technical help or requested information is unknown/missing.',
    'The answer field must be in selected language; JSON keys must stay exactly as specified.',
  ].join(' ');
}

function getLanguageCode(locale) {
  if (locale === 'cs') return 'cs';
  if (locale === 'de') return 'de';
  return 'en';
}

function buildLanguageUserPrefix(locale) {
  const lang = locale === 'cs' ? 'Czech' : locale === 'de' ? 'German' : 'English';
  const code = getLanguageCode(locale);
  return `[LANGUAGE_LOCK code=${code}] Reply strictly in ${lang}.`;
}

function parseToolResponse(rawReply, t) {
  const raw = String(rawReply || '').trim();
  if (!raw) {
    return { cleanReply: t('chat.noReplyFromServer'), showRequestService: true };
  }

  // Prefer full JSON reply.
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const answer = typeof parsed.answer === 'string' ? parsed.answer.trim() : '';
      const needsService = parsed.needsService === true;
      if (answer) {
        return { cleanReply: answer, showRequestService: needsService };
      }
    }
  } catch {
    // no-op; try JSON block extraction below
  }

  // Fallback: extract first JSON object block from wrapped markdown/content.
  const block = raw.match(/\{[\s\S]*\}/);
  if (block) {
    try {
      const parsed = JSON.parse(block[0]);
      const answer = typeof parsed.answer === 'string' ? parsed.answer.trim() : '';
      const needsService = parsed.needsService === true;
      if (answer) {
        return { cleanReply: answer, showRequestService: needsService };
      }
    } catch {
      // no-op
    }
  }

  // Protocol violation: safe fallback is to expose reply and show service action.
  return {
    cleanReply: raw,
    showRequestService: true,
  };
}

export function DeviceHelpChat({ payload, locale, t, onRequestService }) {
  const ctx = useMemo(() => buildDeviceChatContext(payload || {}), [payload]);
  const apiUrl = (process.env.REACT_APP_DEVICE_CHAT_API_URL || '').trim();
  const apiBearer = (process.env.REACT_APP_DEVICE_CHAT_BEARER || '').trim();
  const uploadUrl = useMemo(() => deriveUploadUrl(apiUrl), [apiUrl]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const apiHeaders = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (apiBearer) h.Authorization = `Bearer ${apiBearer}`;
    return h;
  }, [apiBearer]);

  const uploadHeaders = useMemo(() => {
    const h = {};
    if (apiBearer) h.Authorization = `Bearer ${apiBearer}`;
    return h;
  }, [apiBearer]);

  const SUGGESTED_QUESTIONS = useMemo(() => getSuggestedQuestionLabels(locale), [locale]);

  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [pending, setPending] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const hasConversation = messages.length > 0 || pending;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  async function handleSend(overrideText) {
    const raw = typeof overrideText === 'string' ? overrideText : prompt;
    const text = (raw || '').trim();
    if (!text || pending) return;

    const userMsg = { role: 'user', content: text };
    setPrompt('');
    setMessages((m) => [...m, userMsg]);
    setPending(true);

    try {
      const canned = getCannedAnswerForSuggestedQuestion(text, locale);
      if (canned) {
        // Same UX as API: keep typing state for a believable round-trip (not instant).
        const delayMs = 1100 + Math.floor(Math.random() * 1100);
        await new Promise((r) => setTimeout(r, delayMs));
        setMessages((m) => [...m, { role: 'assistant', content: canned }]);
        return;
      }

      if (apiUrl) {
        if (ctx.deviceItemId == null) {
          throw new Error(t('chat.missingItemId'));
        }
        const systemMsg = {
          role: 'system',
          content: buildLanguageSystemPrompt(locale),
        };
        const languageUserPrefix = buildLanguageUserPrefix(locale);
        const conversation = [...messages, userMsg].map(({ role, content }) => ({
          role,
          content:
            role === 'user'
              ? `${languageUserPrefix}\n${content}`
              : content,
        }));
        const contextBlock = `${buildLanguageSystemPrompt(locale)}\n\n${ctx.contextBlock}`;
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: apiHeaders,
          body: JSON.stringify({
            messages: [systemMsg, ...conversation],
            contextBlock,
            deviceItemId: ctx.deviceItemId,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const reply =
          typeof data.reply === 'string'
            ? data.reply
            : typeof data.content === 'string'
              ? data.content
              : t('chat.noReplyFromServer');
        const { cleanReply, showRequestService } = parseToolResponse(reply, t);
        const localizedReply =
          showRequestService ? `${cleanReply}\n\n${t('chat.fallbackText')}` : cleanReply;
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: localizedReply,
            showRequestService,
          },
        ]);
      } else {
        await new Promise((r) => setTimeout(r, 250));
        const mockReply = mockDeviceChatReply(text, ctx);
        const { cleanReply, showRequestService } = parseToolResponse(mockReply, t);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: cleanReply,
            showRequestService,
          },
        ]);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `${t('chat.genericError')} ${e.message || String(e)}`,
          showRequestService: true,
        },
      ]);
    } finally {
      setPending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function onUploadChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !uploadUrl || ctx.deviceItemId == null) return;
    setUploadPending(true);
    setUploadMsg(null);
    try {
      const q = new URLSearchParams({ deviceItemId: String(ctx.deviceItemId) });
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await fetch(`${uploadUrl}?${q.toString()}`, {
        method: 'POST',
        headers: uploadHeaders,
        body: fd,
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (!res.ok) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }
      const n = data.chunksStored != null ? data.chunksStored : '?';
      setUploadMsg(
        `${t('chat.uploadIndexedPrefix')} "${file.name}" (${n} ${t('chat.uploadIndexedSuffix')}`
      );
    } catch (err) {
      setUploadMsg(`${t('chat.uploadFailed')} ${err.message || String(err)}`);
    } finally {
      setUploadPending(false);
    }
  }

  return (
    <div className="dcu-wrap">
      <div className="dcu-meta">
        <p className="dcu-meta-line">
          <span className="dcu-meta-strong">{t('chat.title')}</span>
        </p>
        {/* <p className="dcu-meta-tagline">Our AI assistant is here 24/7 to guide you.</p> */}
        <p className="dcu-meta-tagline">{t('chat.tagline')}</p>
        {uploadUrl && ctx.deviceItemId != null && uploadMsg && (
          <p className="dcu-meta-upload-msg">{uploadMsg}</p>
        )}
      </div>

      {uploadUrl && ctx.deviceItemId != null && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="dcu-sr-only"
          onChange={onUploadChange}
          tabIndex={-1}
          aria-hidden
        />
      )}

      <div className={`dcu-card ${hasConversation ? 'dcu-card-expanded' : 'dcu-card-compact'}`}>
        <div className="dcu-messages">
          {messages.map((message, index) =>
            message.role === 'assistant' ? (
              <div key={index} className="dcu-row-assistant">
                <img src={aiAssistantImg} className="dcu-ai-icon" alt="AI" width={22} height={22} />
                <div className="dcu-assistant-body">
                  <DeviceChatMarkdown content={message.content} />
                  {message.showRequestService && (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={onRequestService}
                      style={{ marginTop: 8 }}
                    >
                      <Wrench size={15} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                      {t('chat.fallbackButton')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div key={index} className="dcu-row-user">
                <div className="dcu-bubble-user">
                  <p>{message.content}</p>
                </div>
              </div>
            )
          )}
          {pending && (
            <div className="dcu-row-assistant">
              <img src={aiAssistantImg} className="dcu-ai-icon" alt="AI" width={22} height={22} />
              <div className="dcu-assistant-body dcu-typing-wrap">
                <div className="dcu-typing">
                  <span className="dcu-dot" />
                  <span className="dcu-dot" />
                  <span className="dcu-dot" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="dcu-composer-outer">
          <div className="dcu-suggestions" aria-label={t('chat.suggestionsAria')}>
            {SUGGESTED_QUESTIONS.map((q) => (
              <button
                key={q.key}
                type="button"
                className="dcu-suggestion-chip"
                disabled={pending}
                onClick={() => handleSend(q.label)}
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="dcu-composer">
            <textarea
              className="dcu-textarea"
              placeholder={t('chat.composerPlaceholder')}
              rows={1}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={pending}
            />
            <div className="dcu-composer-actions">
              <div className="dcu-composer-btns">
                <button
                  type="button"
                  className="dcu-btn dcu-btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!uploadUrl || ctx.deviceItemId == null || uploadPending || pending}
                  title={t('chat.uploadTitle')}
                  aria-label={t('chat.uploadAria')}
                >
                  <Upload className="dcu-icon" strokeWidth={2} />
                </button>
                {prompt.trim().length > 0 ? (
                  <button
                    type="button"
                    className="dcu-btn dcu-btn-send"
                    onClick={() => handleSend()}
                    disabled={pending}
                    aria-label={t('chat.sendAria')}
                  >
                    <Send className="dcu-icon" strokeWidth={2} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dcu-btn dcu-btn-mic"
                    disabled
                    aria-label={t('chat.voiceUnavailableAria')}
                  >
                    <Mic className="dcu-icon" strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="dcu-meta-guide-support">
        {t('chat.footerSupport')}
      </p>
    </div>
  );
}
